export type PeriodView = "date" | "month" | "year";

export const formatPeriodLabel = (period: string, view: PeriodView): string => {
  if (!period) return "";

  const p = String(period).trim();

  // If API already sends year like "2026"
  if (view === "year" && /^\d{4}$/.test(p)) return p;

  // If API already sends month like "2025-08"
  if (view === "month" && /^\d{4}-\d{2}$/.test(p)) {
    const [y, m] = p.split("-");
    return `${m}/${y}`;
  }

  // Try parsing as Date (works for ISO strings)
  const d = new Date(p);
  if (!isNaN(d.getTime())) {
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    if (view === "year") return String(year);
    if (view === "month") return `${month}/${year}`;
    return `${day}/${month}/${year}`; // date
  }

  // Fallback
  return p;
};

export const getPeriodSortValue = (period: string, view: PeriodView): number => {
  if (!period) return 0;

  const p = String(period).trim();

  // Year: "2026" -> Jan 1st
  if (view === "year" && /^\d{4}$/.test(p)) {
    return new Date(Number(p), 0, 1).getTime();
  }

  // Month: "2025-08" -> 1st of that month
  if (view === "month" && /^\d{4}-\d{2}$/.test(p)) {
    const [y, m] = p.split("-");
    return new Date(Number(y), Number(m) - 1, 1).getTime();
  }

  // Date/ISO
  const d = new Date(p);
  if (!isNaN(d.getTime())) return d.getTime();

  return 0;
};

/**
 * Optional convenience helper to map + sort in one place.
 * You can reuse this in all charts to keep code short.
 */
export const mapAndSortLeadCount = <T extends { period: string }>(
  leadCount: T[],
  view: PeriodView
) => {
  const mapped = (leadCount || []).map((item) => ({
    ...item,
    period: formatPeriodLabel(item.period, view),
    _periodSort: getPeriodSortValue(item.period, view),
  }));

  mapped.sort((a, b) => a._periodSort - b._periodSort);
  return mapped;
};